import { useCallback, useEffect, useMemo, useState } from "react";

import productsData from "../../home/definitions/ProductsData";
import { getProducts } from "../../../gateway/http/requests/ProductsRequests";
import type {
  ProductDefinition,
  ProductsApiResponse,
} from "../types/productTypes";
import {
  getProductById,
  mergeProductsWithPrices,
  slugifyProductTitle,
} from "../utils/productUtils";

export const useProductDetails = (
  productId: string | number,
  onProductChange: (
    id: string | number,
    fullPrice: number | null,
    title: string
  ) => void
) => {
  const initialProducts = productsData as ProductDefinition[];

  const [products, setProducts] = useState<ProductDefinition[]>(initialProducts);
  const [product, setProduct] = useState<ProductDefinition>(() =>
    getProductById(initialProducts, productId)
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  const isMenuOpen = Boolean(anchorEl);

  const pageLocation = useMemo(() => {
    return `/produto/${slugifyProductTitle(product.title)}`;
  }, [product.title]);

  const productsMenu = useMemo(() => {
    return products
      .filter((item) => String(item.id) !== String(product.id))
      .map((item) => item.title);
  }, [products, product.id]);

  const openMenu = useCallback((element: HTMLElement) => {
    setAnchorEl(element);
  }, []);

  const closeMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const selectProduct = useCallback(
    (productTitle: string) => {
      const selectedProduct = products.find(
        (item) => item.title === productTitle
      );

      if (!selectedProduct) {
        closeMenu();
        return;
      }

      setProduct(selectedProduct);

      onProductChange(
        selectedProduct.id,
        selectedProduct.fullPrice,
        selectedProduct.title
      );

      closeMenu();
    },
    [products, closeMenu, onProductChange]
  );

  const loadProducts = useCallback(async () => {
    setIsLoadingProducts(true);

    try {
      const response = await getProducts("430");

      if (response?.status !== 200 && response?.status !== 201) {
        return;
      }

      const responseData = response.data as ProductsApiResponse | undefined;
      const plans = responseData?.retorno ?? [];

      const newProducts = mergeProductsWithPrices(initialProducts, plans);
      const newProduct = getProductById(newProducts, productId);

      setProducts(newProducts);
      setProduct(newProduct);

      onProductChange(newProduct.id, newProduct.fullPrice, newProduct.title);
    } finally {
      setIsLoadingProducts(false);
    }
  }, [initialProducts, productId, onProductChange]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  return {
    product,
    productsMenu,
    pageLocation,
    anchorEl,
    isMenuOpen,
    isLoadingProducts,
    openMenu,
    closeMenu,
    selectProduct,
  };
};